import { useEffect, useMemo, useState } from "react";
import { useSettings } from "../settings/context";
import { overview, type OverviewRow } from "../settings/overview";
import { EVERY_SCREEN } from "../routes/registry";
import { ORDER_KEY, orderedEntries } from "../funnel/order";
import { addedInstances } from "../pages/repeatable";

/**
 * THE RECAP — what is set, and where the typologies part ways.
 *
 * The panel answers one setting at a time. This answers the question you only ask
 * after an hour's work: what have I changed, and what does an exhibitor see that
 * everyone else does not? An override forgotten in a corner is otherwise found by
 * a participant rather than by its author.
 *
 * Two views, and the ORDER IS THE POINT — divergences first. "Everything set" is
 * a long list you read when you want to check your work; "what differs" is the
 * short one that tells you something you did not already know.
 *
 * ⚠️ ONE LANGUAGE AT A TIME, the one being read, and the modal says so. A table
 * crossing both axes of the cascade would have as many columns as languages
 * times typologies, and nobody reads that.
 *
 * Displayed strings are French: the organiser is a French speaker.
 */

/**
 * The screen's own name, from the registry — never its catalog key.
 *
 * ⚠️ THE SITE'S PAGES INCLUDED. The badge has settings of its own, hence a section of
 * its own in this table: looked up in the steps alone, its heading fell back to the raw
 * key ("badge") in the middle of readable names.
 */
function screenName(scope: string): string {
  if (scope === "site") return "Le site";
  return EVERY_SCREEN.find((s) => s.key === scope)?.label ?? scope;
}

type Forms = ReturnType<typeof useSettings>["forms"];

const FORM_TYPES: ReadonlySet<OverviewRow["type"]> = new Set([
  "select-forms-user",
  "select-forms-company",
]);

/**
 * A value as an organiser would read it, never as JSON, and for a choice, what
 * was CHOSEN rather than what is stored.
 *
 * ⚠️ UN FORMULAIRE SE NOMME D'APRÈS CEUX DE L'ÉVÉNEMENT, comme dans l'étape : le
 * récapitulatif montrait « 5693 » là où l'étape montre « Inscription Matinée
 * découverte » (bug #293). Un formulaire qui n'existe plus garde son numéro, « #id »,
 * comme le « Hors liste » de la ligne du panneau. Les autres choix prennent le
 * libellé de leur option, et une valeur hors liste reste lisible telle quelle.
 */
function read(value: unknown, row: OverviewRow, forms: Forms): string {
  if (value === undefined || value === null || value === "") return "vide";
  if (value === true) return "Oui";
  if (value === false) return "Non";
  const label = (v: unknown): string => {
    const id = String(v);
    if (FORM_TYPES.has(row.type))
      return forms.find((f) => String(f.id) === id)?.name ?? `#${id}`;
    return row.options?.find((o) => o.value === id)?.label ?? id;
  };
  if (FORM_TYPES.has(row.type) || row.options) {
    if (!Array.isArray(value)) return label(value);
    return value.length ? value.map(label).join(", ") : "vide";
  }
  return String(value);
}

/** Line breaks would blow the row height apart; the full text stays in `title`. */
function show(value: unknown, row: OverviewRow, forms: Forms): string {
  return read(value, row, forms).replace(/\s*\n\s*/g, " · ");
}

function Table({
  rows,
  typos,
}: {
  rows: OverviewRow[];
  typos: ReadonlyArray<{ id: number; name: string }>;
}) {
  const { forms } = useSettings();
  return (
    <table className="ovw__t">
      <thead>
        <tr>
          <th>Réglage</th>
          <th>Commun</th>
          {typos.map((t) => (
            <th key={t.id}>{t.name}</th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <th scope="row">
              {row.label}
              <small>
                {screenName(row.scope)} · {row.group}
                {row.section ? ` · ${row.section}` : ""}
              </small>
            </th>
            <td title={read(row.common, row, forms)}>
              {show(row.common, row, forms)}
            </td>
            {row.cells.map((cell) => (
              <td
                key={cell.typoId}
                title={read(cell.value, row, forms)}
                /* `own` says the value is WRITTEN here, `differs` that it changes
                   something. Both are worth seeing: an override written to the
                   same value changes nothing and is exactly what you came to
                   clean up. */
                data-own={cell.own ? "1" : undefined}
              >
                {show(cell.value, row, forms)}
              </td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export function Overview({ onClose }: { onClose: () => void }) {
  const [tab, setTab] = useState<"differences" | "all">("differences");
  const { rows, scope, languages, typos, value } = useSettings();

  const named = typos.filter(
    (t): t is { id: number; name: string } => t.id !== null,
  );
  const all = useMemo(
    // Le parcours rangé accompagne les typologies : sans lui, une surcharge posée sur la
    // 2e étape « informations » n'apparaîtrait dans aucun des deux onglets. Les
    // pages ajoutées, pour la même raison.
    () =>
      overview(
        rows,
        scope,
        named,
        orderedEntries(value(ORDER_KEY)),
        addedInstances(value),
      ),
    // `named` is rebuilt each render; its ids are what matter and they are fixed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, scope],
  );

  const differing = all.filter((r) => r.differs);
  const set = all.filter((r) => r.isSet);
  const shown = tab === "differences" ? differing : set;
  const languageName =
    languages.find((l) => l.id === scope.languageId)?.name ?? "";

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="ovw"
      role="dialog"
      aria-modal="true"
      aria-label="Récapitulatif"
    >
      {/* The backdrop closes it. A modal you can only leave through one small
          cross is a modal people learn to dread. */}
      <button
        type="button"
        className="ovw__back"
        aria-label="Fermer le récapitulatif"
        onClick={onClose}
      />

      <div className="ovw__box">
        <div className="ovw__h">
          <div>
            <b>Récapitulatif</b>
            {/* Named, not inflected: "Version française" would need a gendered
                adjective per language, and "Version anglais" is how that breaks. */}
            <span>Langue&nbsp;: {languageName}</span>
          </div>
          <button
            type="button"
            className="spot__x"
            onClick={onClose}
            aria-label="Fermer"
          >
            ✕
          </button>
        </div>

        <div
          className="ovw__tabs"
          role="group"
          aria-label="Ce que montre le récapitulatif"
        >
          <button
            type="button"
            aria-pressed={tab === "differences"}
            onClick={() => setTab("differences")}
          >
            Différences entre typologies <i>{differing.length}</i>
          </button>
          <button
            type="button"
            aria-pressed={tab === "all"}
            onClick={() => setTab("all")}
          >
            Tout ce qui est réglé <i>{set.length}</i>
          </button>
        </div>

        <div className="ovw__b">
          {shown.length > 0 ? (
            <Table rows={shown} typos={named} />
          ) : (
            /* SAY WHY it is empty. An empty table looks broken; "no typology
               differs from the others" is an answer, and a reassuring one. */
            <p className="norule">
              {tab === "differences" ? (
                <>
                  <b>Aucune typologie ne voit autre chose.</b>
                  <br />
                  Tous les participants lisent les mêmes valeurs dans cette
                  langue.
                </>
              ) : (
                <>
                  <b>Rien n’est encore réglé.</b>
                  <br />
                  Les valeurs affichées sur le site sont celles d’origine.
                </>
              )}
            </p>
          )}
        </div>

        {tab === "differences" && differing.length > 0 && (
          <p className="ovw__f">
            Une case soulignée porte une valeur écrite pour cette typologie. Les
            autres sont héritées.
          </p>
        )}
      </div>
    </div>
  );
}
